/**
 * Đợi element tour xuất hiện và có kích thước thật.
 * Dùng rAF thay vì setTimeout để phản hồi nhanh hơn.
 * Với Popper/Grow: chờ 2 frame liên tiếp có cùng rect để tránh đo giữa animation.
 */
export const waitForTourElement = (
  selector,
  { timeout = 2000, stableFrames = 2 } = {}
) =>
  new Promise((resolve, reject) => {
    const start = Date.now();
    let lastKey = "";
    let stableCount = 0;

    const tick = () => {
      const el = document.querySelector(selector);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          const key = `${Math.round(rect.top)}:${Math.round(rect.left)}:${Math.round(rect.width)}:${Math.round(rect.height)}`;
          if (key === lastKey) {
            stableCount += 1;
          } else {
            lastKey = key;
            stableCount = 1;
          }
          if (stableCount >= stableFrames) {
            resolve(el);
            return;
          }
        } else {
          lastKey = "";
          stableCount = 0;
        }
      }
      if (Date.now() - start >= timeout) {
        reject(new Error(`Tour element not found: ${selector}`));
        return;
      }
      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  });

/** Chờ N frame để layout ổn định (nhanh hơn setTimeout cố định) */
const waitFrames = (count = 1) =>
  new Promise((resolve) => {
    let left = count;
    const frame = () => {
      left -= 1;
      if (left <= 0) resolve();
      else requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  });

/** Đưa element vào viewport rồi chờ layout ổn định sau scroll */
const scrollTourElementIntoView = async (el, options = {}) => {
  if (!el || typeof el.scrollIntoView !== "function") return;

  el.scrollIntoView({
    behavior: "instant" in document.documentElement.style ? "instant" : "auto",
    block: options.block ?? "center",
    inline: options.inline ?? "nearest",
  });
  await waitFrames(2);
};

/**
 * Chuẩn bị step đích: mở dialog/menu (nếu có) và chờ DOM sẵn sàng.
 * Metadata: tourOpen, tourWaitFor, tourWaitFrames, tourStableFrames, tourScrollBlock
 * Luôn chờ ổn định SAU tourOpen — tránh Prev đo nhầm khi dialog vừa remount.
 */
const prepareTourStep = async (targetStep) => {
  if (!targetStep) return;

  const needsOpen = typeof targetStep.tourOpen === "function";
  const waitSelector = targetStep.tourWaitFor;

  try {
    if (needsOpen) {
      await targetStep.tourOpen();
    }

    if (waitSelector) {
      const el = await waitForTourElement(waitSelector, {
        stableFrames: targetStep.tourStableFrames ?? 2,
      });
      // Scroll vào view trước khi Reactour đo — tránh highlight bị cắt (vd. tab trên đầu trang)
      if (targetStep.tourScroll !== false) {
        await scrollTourElementIntoView(el, {
          block: targetStep.tourScrollBlock ?? "center",
        });
        // Đo lại sau scroll vì rect đã đổi
        await waitForTourElement(waitSelector, {
          stableFrames: targetStep.tourStableFrames ?? 2,
        });
      }
      await waitFrames(targetStep.tourWaitFrames ?? 1);
    }
  } catch (error) {
    console.warn(error);
  }
};

/**
 * Next: chuẩn bị step tiếp theo rồi mới chuyển.
 */
export const advanceTourStep = async ({
  setCurrentStep,
  currentStep,
  stepsLength,
  steps,
}) => {
  const nextIndex = Math.min(currentStep + 1, stepsLength - 1);
  if (nextIndex === currentStep) return;

  await prepareTourStep(steps?.[nextIndex]);
  setCurrentStep(nextIndex);
};

/**
 * Prev: chuẩn bị step trước (vd. mở lại dialog/menu) rồi mới chuyển.
 */
export const retreatTourStep = async ({
  setCurrentStep,
  currentStep,
  steps,
}) => {
  const prevIndex = Math.max(currentStep - 1, 0);
  if (prevIndex === currentStep) return;

  await prepareTourStep(steps?.[prevIndex]);
  setCurrentStep(prevIndex);
};
